import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface KeyedImageProps {
    /** Artwork shot on a flat magenta background, served from /public. */
    src: string
    width: number
    height: number
    alt: string
    className?: string
}

/** Widths the built-in image optimizer accepts (Next.js deviceSizes). */
const OPTIMIZER_WIDTHS = [640, 750, 828, 1080, 1200, 1920]

/** How strongly a pixel leans magenta (red and blue both above green) before it starts to fade, and where it is gone. */
const KEY_START = 40
const KEY_END = 115

/** Removes the magenta backdrop in place and neutralizes the magenta tint it leaves on edges. */
const keyMagenta = (pixels: Uint8ClampedArray): void => {
    for (let index = 0; index < pixels.length; index += 4) {
        const red = pixels[index]
        const green = pixels[index + 1]
        const blue = pixels[index + 2]
        const spill = Math.min(red, blue) - green

        if (spill <= 0) continue

        pixels[index] = red - spill
        pixels[index + 2] = blue - spill

        if (spill > KEY_START) {
            pixels[index + 3] = spill >= KEY_END
                ? 0
                : Math.round(pixels[index + 3] * (1 - (spill - KEY_START) / (KEY_END - KEY_START)))
        }
    }
}

/**
 * Draws a character that was generated on magenta, with the magenta keyed out in the browser,
 * so it can sit in front of other layers like a cut-out.
 */
export const KeyedImage: FC<KeyedImageProps> = ({ src, width, height, alt, className }): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [ready, setReady] = useState(false)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        let cancelled = false

        const shownHeight = canvas.getBoundingClientRect().height || window.innerHeight
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        const neededWidth = (shownHeight * ratio * width) / height
        const optimizedWidth = OPTIMIZER_WIDTHS.find((candidate) => candidate >= neededWidth) ?? OPTIMIZER_WIDTHS[OPTIMIZER_WIDTHS.length - 1]
        const sources = [`/_next/image?url=${encodeURIComponent(src)}&w=${optimizedWidth}&q=75`, src]

        const draw = (image: HTMLImageElement): boolean => {
            const context = canvas.getContext('2d', { willReadFrequently: true })
            if (!context) return false

            canvas.width = image.naturalWidth
            canvas.height = image.naturalHeight
            context.drawImage(image, 0, 0)

            try {
                const frame = context.getImageData(0, 0, canvas.width, canvas.height)
                keyMagenta(frame.data)
                context.putImageData(frame, 0, 0)
            }
            catch {
                return false
            }

            return true
        }

        const load = (attempt: number): void => {
            if (cancelled || attempt >= sources.length) return

            const image = new Image()
            image.decoding = 'async'

            image.onload = () => {
                if (cancelled) return
                if (draw(image)) setReady(true)
                else load(attempt + 1)
            }

            image.onerror = () => load(attempt + 1)
            image.src = sources[attempt]
        }

        load(0)

        return () => {
            cancelled = true
        }
    }, [src, width, height])

    return (
        <canvas
            ref={canvasRef}
            role='img'
            aria-label={alt}
            width={Math.round(width / 16)}
            height={Math.round(height / 16)}
            className={cn(
                'block h-full w-full',
                ready ? 'animate-keyed-reveal motion-reduce:animate-none' : 'opacity-0',
                className
            )}
        />
    )
}
